import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { FormativeAssessmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface FormativeAssessmentCardProps {
  assessment: FormativeAssessmentResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
  },
  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  // Pedagogical Approach section styles
  pedagogicalHeader: {
    backgroundColor: "#dbeafe", // Blue light
    borderLeftWidth: 4,
    borderLeftColor: "#3b82f6", // Blue
  },
  pedagogicalTitle: {
    color: "#2563eb", // Blue darker
    fontSize: 14,
    fontWeight: "bold",
  },
  // Learning Phases section styles
  phasesHeader: {
    backgroundColor: "#dcfce7", // Green light
    borderLeftWidth: 4,
    borderLeftColor: "#22c55e", // Green
  },
  phasesTitle: {
    color: "#16a34a", // Green darker
    fontSize: 14,
    fontWeight: "bold",
  },
  // Assessment Methodology section styles
  methodologyHeader: {
    backgroundColor: "#fef3c7", // Amber light
    borderLeftWidth: 4,
    borderLeftColor: "#f59e0b", // Amber
  },
  methodologyTitle: {
    color: "#d97706", // Amber darker
    fontSize: 14,
    fontWeight: "bold",
  },
  // Pedagogical Feedback section styles
  feedbackHeader: {
    backgroundColor: "#fef9c3", // Yellow light
    borderLeftWidth: 4,
    borderLeftColor: "#eab308", // Yellow
  },
  feedbackTitle: {
    color: "#ca8a04", // Yellow darker
    fontSize: 14,
    fontWeight: "bold",
  },
  // Learning Reflection section styles
  reflectionHeader: {
    backgroundColor: "#f3e8ff", // Purple light
    borderLeftWidth: 4,
    borderLeftColor: "#a855f7", // Purple
  },
  reflectionTitle: {
    color: "#9333ea", // Purple darker
    fontSize: 14,
    fontWeight: "bold",
  },
  // Phase item styles
  phaseContainer: {
    marginBottom: 14,
    padding: 10,
    backgroundColor: "#f0fdf4", // Green ultra light
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: "#22c55e", // Green
  },
  phaseHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: "#d1fae5", // Green lighter
  },
  phaseLeft: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
  },
  phaseNumber: {
    width: 20,
    height: 20,
    backgroundColor: "#16a34a", // Green darker
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  phaseNumberText: {
    color: "#ffffff",
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "center",
  },
  phaseName: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#15803d", // Green dark
    flex: 1,
  },
  phaseDuration: {
    fontSize: 9,
    color: "#166534", // Green darker
    backgroundColor: "#dcfce7", // Green lighter
    padding: "2 6",
    borderRadius: 10,
  },
  phaseContent: {
    paddingLeft: 28,
  },
  // Methodology & Feedback list item styles
  listItemContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  listItemNumber: {
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  listItemNumberText: {
    color: "#ffffff",
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "center",
  },
  methodologyListItemNumber: {
    backgroundColor: "#f59e0b", // Amber
  },
  feedbackListItemNumber: {
    backgroundColor: "#eab308", // Yellow
  },
  reflectionListItemNumber: {
    backgroundColor: "#a855f7", // Purple
  },
  listItemContent: {
    flex: 1,
  },
  // Old/legacy section styles (keeping for backward compatibility)
  processHeader: {
    backgroundColor: "#dbeafe",
  },
  processTitle: {
    color: "#2563eb",
    fontSize: 11,
    fontWeight: "bold",
  },
  toolHeader: {
    backgroundColor: "#dcfce7",
  },
  toolTitle: {
    color: "#16a34a",
    fontSize: 11,
    fontWeight: "bold",
  },
  oldFeedbackHeader: {
    backgroundColor: "#f3e8ff",
  },
  oldFeedbackTitle: {
    color: "#9333ea",
    fontSize: 11,
    fontWeight: "bold",
  },
  oldReflectionHeader: {
    backgroundColor: "#fef3c7",
  },
  oldReflectionTitle: {
    color: "#d97706",
    fontSize: 11,
    fontWeight: "bold",
  },
});

const FormativeAssessmentDocument = ({
  assessment,
  customizationSettings,
}: FormativeAssessmentCardProps) => {
  const { t } = useTranslation();
  const markdownSectionStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
    listItem: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  // Check if we should use the new or old format sections
  const useNewFormat = Boolean(
    assessment.instructionalFlow?.pedagogical_approach ||
      (assessment.instructionalFlow?.learning_phases &&
        assessment.instructionalFlow.learning_phases.length > 0) ||
      (assessment.assessmentMethodology &&
        assessment.assessmentMethodology.length > 0) ||
      (assessment.pedagogicalFeedback &&
        assessment.pedagogicalFeedback.length > 0) ||
      (assessment.learningReflection &&
        assessment.learningReflection.length > 0),
  );

  // Helper function to scale fontSize based on customizationSettings
  const scaledFontSize = (size: number) =>
    scaleFontSize(size, customizationSettings?.fontSize);

  return (
    <PdfLayout
      courseName={assessment.courseName || ""}
      gradeLevel={assessment.gradeLevel || 0}
      topic={assessment.title || ""}
      imageUrl={assessment.imageUrl}
      headerTextColor="#ca8a04"
      customizationSettings={customizationSettings}
      author={assessment.authorName || ""}
      createdDate={assessment.createdAt || ""}
    >
      <View style={styles.container}>
        {useNewFormat ? (
          // New format sections
          <>
            {/* Pedagogical Approach Section */}
            {assessment.instructionalFlow?.pedagogical_approach && (
              <View style={styles.section} wrap={false}>
                <View style={[styles.sectionHeader, styles.pedagogicalHeader]}>
                  <Text
                    style={[
                      styles.pedagogicalTitle,
                      {
                        fontSize: scaledFontSize(
                          styles.pedagogicalTitle.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("formativeAssessment.pedagogicalApproach")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={assessment.instructionalFlow.pedagogical_approach}
                  customStyle={markdownSectionStyle}
                />
              </View>
            )}

            {/* Learning Phases Section */}
            {assessment.instructionalFlow?.learning_phases &&
              assessment.instructionalFlow.learning_phases.length > 0 && (
                <View style={styles.section} wrap={false}>
                  <View style={[styles.sectionHeader, styles.phasesHeader]}>
                    <Text
                      style={[
                        styles.phasesTitle,
                        {
                          fontSize: scaledFontSize(styles.phasesTitle.fontSize),
                        },
                      ]}
                    >
                      {t("formativeAssessment.learningPhases")}
                    </Text>
                  </View>

                  {assessment.instructionalFlow.learning_phases.map(
                    (phase, index) => (
                      <View
                        key={index}
                        style={styles.phaseContainer}
                        wrap={false}
                      >
                        <View style={styles.phaseHeader}>
                          <View style={styles.phaseLeft}>
                            <View style={styles.phaseNumber}>
                              <Text
                                style={[
                                  styles.phaseNumberText,
                                  {
                                    fontSize: scaledFontSize(
                                      styles.phaseNumberText.fontSize,
                                    ),
                                  },
                                ]}
                              >
                                {index + 1}
                              </Text>
                            </View>
                            <Text
                              style={[
                                styles.phaseName,
                                {
                                  fontSize: scaledFontSize(
                                    styles.phaseName.fontSize,
                                  ),
                                },
                              ]}
                            >
                              {phase.phase_name || `Phase ${index + 1}`}
                            </Text>
                          </View>
                          {phase.duration && (
                            <Text
                              style={[
                                styles.phaseDuration,
                                {
                                  fontSize: scaledFontSize(
                                    styles.phaseDuration.fontSize,
                                  ),
                                },
                              ]}
                            >
                              {phase.duration}
                            </Text>
                          )}
                        </View>
                        {phase.instructional_narrative && (
                          <View style={styles.phaseContent}>
                            <MarkdownRenderer
                              content={phase.instructional_narrative}
                              customStyle={markdownSectionStyle}
                            />
                          </View>
                        )}
                      </View>
                    ),
                  )}
                </View>
              )}

            {/* Assessment Methodology Section */}
            {assessment.assessmentMethodology &&
              assessment.assessmentMethodology.length > 0 && (
                <View style={styles.section} wrap={false}>
                  <View
                    style={[styles.sectionHeader, styles.methodologyHeader]}
                  >
                    <Text
                      style={[
                        styles.methodologyTitle,
                        {
                          fontSize: scaledFontSize(
                            styles.methodologyTitle.fontSize,
                          ),
                        },
                      ]}
                    >
                      {t("formativeAssessment.assessmentMethodology")}
                    </Text>
                  </View>

                  {assessment.assessmentMethodology.map((method, index) => (
                    <View
                      key={index}
                      style={styles.listItemContainer}
                      wrap={false}
                    >
                      <View
                        style={[
                          styles.listItemNumber,
                          styles.methodologyListItemNumber,
                        ]}
                      >
                        <Text
                          style={[
                            styles.listItemNumberText,
                            {
                              fontSize: scaledFontSize(
                                styles.listItemNumberText.fontSize,
                              ),
                            },
                          ]}
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <View style={styles.listItemContent}>
                        <MarkdownRenderer
                          content={method}
                          customStyle={markdownSectionStyle}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}

            {/* Pedagogical Feedback Section */}
            {assessment.pedagogicalFeedback &&
              assessment.pedagogicalFeedback.length > 0 && (
                <View style={styles.section} wrap={false}>
                  <View style={[styles.sectionHeader, styles.feedbackHeader]}>
                    <Text
                      style={[
                        styles.feedbackTitle,
                        {
                          fontSize: scaledFontSize(
                            styles.feedbackTitle.fontSize,
                          ),
                        },
                      ]}
                    >
                      {t("formativeAssessment.pedagogicalFeedback")}
                    </Text>
                  </View>

                  {assessment.pedagogicalFeedback.map((feedback, index) => (
                    <View
                      key={index}
                      style={styles.listItemContainer}
                      wrap={false}
                    >
                      <View
                        style={[
                          styles.listItemNumber,
                          styles.feedbackListItemNumber,
                        ]}
                      >
                        <Text
                          style={[
                            styles.listItemNumberText,
                            {
                              fontSize: scaledFontSize(
                                styles.listItemNumberText.fontSize,
                              ),
                            },
                          ]}
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <View style={styles.listItemContent}>
                        <MarkdownRenderer
                          content={feedback}
                          customStyle={markdownSectionStyle}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}

            {/* Learning Reflection Section */}
            {assessment.learningReflection &&
              assessment.learningReflection.length > 0 && (
                <View style={styles.section} wrap={false}>
                  <View style={[styles.sectionHeader, styles.reflectionHeader]}>
                    <Text
                      style={[
                        styles.reflectionTitle,
                        {
                          fontSize: scaledFontSize(
                            styles.reflectionTitle.fontSize,
                          ),
                        },
                      ]}
                    >
                      {t("formativeAssessment.learningReflection")}
                    </Text>
                  </View>

                  {assessment.learningReflection.map((reflection, index) => (
                    <View
                      key={index}
                      style={styles.listItemContainer}
                      wrap={false}
                    >
                      <View
                        style={[
                          styles.listItemNumber,
                          styles.reflectionListItemNumber,
                        ]}
                      >
                        <Text
                          style={[
                            styles.listItemNumberText,
                            {
                              fontSize: scaledFontSize(
                                styles.listItemNumberText.fontSize,
                              ),
                            },
                          ]}
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <View style={styles.listItemContent}>
                        <MarkdownRenderer
                          content={reflection}
                          customStyle={markdownSectionStyle}
                        />
                      </View>
                    </View>
                  ))}
                </View>
              )}
          </>
        ) : (
          // Legacy format sections (for backward compatibility)
          <>
            {/* Process Section */}
            {assessment.process && (
              <View style={styles.section}>
                <View style={[styles.sectionHeader, styles.processHeader]}>
                  <Text
                    style={[
                      styles.processTitle,
                      {
                        fontSize: scaledFontSize(styles.processTitle.fontSize),
                      },
                    ]}
                  >
                    {t("formativeAssessment.process")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={assessment.process}
                  customStyle={markdownSectionStyle}
                />
              </View>
            )}

            {/* Assessment Tool Section */}
            {assessment.assessmentTool && (
              <View style={styles.section} wrap={false}>
                <View style={[styles.sectionHeader, styles.toolHeader]}>
                  <Text
                    style={[
                      styles.toolTitle,
                      { fontSize: scaledFontSize(styles.toolTitle.fontSize) },
                    ]}
                  >
                    {t("formativeAssessment.assessmentTool")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={assessment.assessmentTool}
                  customStyle={markdownSectionStyle}
                />
              </View>
            )}

            {/* Teacher Feedback Section */}
            {assessment.teacherFeedback && (
              <View style={styles.section} wrap={false}>
                <View style={[styles.sectionHeader, styles.oldFeedbackHeader]}>
                  <Text
                    style={[
                      styles.oldFeedbackTitle,
                      {
                        fontSize: scaledFontSize(
                          styles.oldFeedbackTitle.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("formativeAssessment.teacherFeedback")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={assessment.teacherFeedback}
                  customStyle={markdownSectionStyle}
                />
              </View>
            )}

            {/* Student Reflection Section */}
            {assessment.studentReflection && (
              <View style={styles.section} wrap={false}>
                <View
                  style={[styles.sectionHeader, styles.oldReflectionHeader]}
                >
                  <Text
                    style={[
                      styles.oldReflectionTitle,
                      {
                        fontSize: scaledFontSize(
                          styles.oldReflectionTitle.fontSize,
                        ),
                      },
                    ]}
                  >
                    {t("formativeAssessment.studentReflection")}
                  </Text>
                </View>
                <MarkdownRenderer
                  content={assessment.studentReflection}
                  customStyle={markdownSectionStyle}
                />
              </View>
            )}
          </>
        )}
      </View>
    </PdfLayout>
  );
};

function FormativeAssessmentCardPDF(props: FormativeAssessmentCardProps) {
  return <FormativeAssessmentDocument {...props} />;
}

export default FormativeAssessmentCardPDF;
